// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { Upload } from 'lucide-react';
import { MAX_FILE_SIZE_BYTES, ACCEPT_MAP } from '../../utils/constants';

export function FileUploadForm({
  fileType,
  file,
  onFileChange,
  onUpload,
  uploading,
  message,
  progress,
  isEncrypted,
  onEncryptedChange,
  password,
  onPasswordChange,
  showPassword,
  onPasswordHoldStart,
  onPasswordHoldEnd,
  banks = [],
  bankName = '',
  onBankChange,
}) {
  const isSuccess = message?.toLowerCase().includes('success');
  const { label, accept } = ACCEPT_MAP[fileType];
  const isPdf = fileType === 'pdf';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onUpload();
      }}
      className="space-y-5"
    >
      {/* Drop zone */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          {label} file
        </label>
        <div
          onDrop={(e) => {
            e.preventDefault();
            onFileChange(e.dataTransfer?.files?.[0] ?? null);
          }}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-white hover:border-blue-400 transition-colors"
        >
          <Upload className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">
            Drag & drop your {label} here, or
          </p>
          <label className="inline-block cursor-pointer">
            <span className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm hover:bg-blue-800 transition-colors">
              Browse files
            </span>
            <input
              type="file"
              accept={accept}
              data-testid="file-input"
              onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
              className="hidden"
            />
          </label>
          <p className="text-xs text-gray-400 mt-3">
            {accept.split(',').filter((a) => a.startsWith('.')).join(', ')} only · Max{' '}
            {MAX_FILE_SIZE_BYTES / 1024 / 1024}MB
          </p>
        </div>

        {/* Selected file pill */}
        {file && (
          <div className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
            <div className="min-w-0">
              <p className="text-sm font-medium text-blue-900 truncate">
                {file.name}
              </p>
              <p className="text-xs text-blue-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <button
              type="button"
              onClick={() => onFileChange(null)}
              className="ml-3 text-blue-400 hover:text-blue-600 text-lg leading-none shrink-0"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Bank (statements only; PDFs are parsed without a column map) */}
      {!isPdf && (
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-2">Bank</span>
          <select
            value={bankName}
            onChange={(e) => onBankChange?.(e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Auto-detect</option>
            {banks
              .filter((b) => b !== 'GENERIC')
              .map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            <option value="GENERIC">Other bank (generic columns)</option>
          </select>
        </label>
      )}

      {/* Encrypted toggle (PDF only) */}
      {isPdf && (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={isEncrypted}
          onChange={(e) => onEncryptedChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
        <span className="text-sm text-gray-700">
          This file is password-protected
        </span>
      </label>
      )}

      {/* Password field */}
      {isPdf && isEncrypted && (
        <div className="flex items-center gap-2">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Enter file password"
            className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onMouseDown={onPasswordHoldStart}
            onMouseUp={onPasswordHoldEnd}
            onMouseLeave={onPasswordHoldEnd}
            onTouchStart={onPasswordHoldStart}
            onTouchEnd={onPasswordHoldEnd}
            aria-label="Hold to reveal password"
            className="h-9 w-9 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center text-sm select-none shrink-0"
          >
            👁
          </button>
        </div>
      )}

      {/* Progress bar */}
      {uploading && (
        <div className="space-y-1">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 text-center">
            Processing… {progress}%
          </p>
        </div>
      )}

      {/* Message */}
      {message && (
        <p
          className={`text-sm text-center ${isSuccess ? 'text-green-600' : 'text-red-500'}`}
        >
          {message}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!file || uploading || (isEncrypted && !password)}
        className="w-full py-2.5 bg-blue-700 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {uploading ? (
          <span className="flex items-center justify-center gap-2">
            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
            Uploading…
          </span>
        ) : (
          `Upload ${label}`
        )}
      </button>
    </form>
  );
}
